import React, { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Typography, Box, Alert, CircularProgress, Stepper, Step, StepLabel, Paper, Tabs, Tab } from '@mui/material';
import { Icon } from '@iconify/react';
interface AmendTransactionDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (data: {
    newAccount: string;
    fetchedTitle: string;
    reason: string;
  }) => Promise<void>;
  transaction: any;
}
const titleSteps = ['Enter IBAN', 'Verify Title', 'Submit Reason'];
const accountSteps = ['Enter New Account', 'Submit Reason'];
export const AmendTransactionDialog: React.FC<AmendTransactionDialogProps> = ({
  open,
  onClose,
  onConfirm,
  transaction
}) => {
  const [tabIndex, setTabIndex] = useState(0);
  const [activeStep, setActiveStep] = useState(0);
  const [newAccount, setNewAccount] = useState('');
  const [fetchedTitle, setFetchedTitle] = useState('');
  const [reason, setReason] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
    setActiveStep(0);
    setNewAccount('');
    setFetchedTitle('');
    setReason('');
    setError(null);
  };
  const handleFetchTitle = async () => {
    if (!newAccount.trim()) {
      setError('IBAN is required');
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 800));
      setFetchedTitle('MUHAMMAD ALI KHAN');
      setActiveStep(1);
    } catch (err) {
      setError('Failed to fetch account title');
    } finally {
      setIsLoading(false);
    }
  };
  const handleRejectTitle = () => {
    setFetchedTitle('');
    setNewAccount('');
    setActiveStep(0);
  };
  const handleAcceptTitle = () => {
    setActiveStep(2);
  };
  const handleNextAccountStep = () => {
    if (!newAccount.trim()) {
      setError('New account number is required');
      return;
    }
    setActiveStep(1);
  };
  const handleSubmit = async () => {
    if (!reason.trim()) {
      setError('Amendment reason is required');
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      await onConfirm({
        newAccount,
        fetchedTitle: tabIndex === 0 ? fetchedTitle : transaction.beneficiaryName,
        reason
      });
      handleClose();
    } catch (err) {
      setError('Failed to submit amendment request');
    } finally {
      setIsLoading(false);
    }
  };
  const handleClose = () => {
    if (!isLoading) {
      setTabIndex(0);
      setActiveStep(0);
      setNewAccount('');
      setFetchedTitle('');
      setReason('');
      setError(null);
      onClose();
    }
  };
  if (!transaction) return null;
  return <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth data-id="element-36">
      <DialogTitle sx={{
      borderBottom: '1px solid #E5E7EB',
      pb: 2,
      fontWeight: 700,
      color: '#1F2937'
    }} data-id="element-37">
        Amend Transaction
      </DialogTitle>
      <DialogContent data-id="element-38">
        <Box sx={{
        mt: 1,
        mb: 2
      }} data-id="element-39">
          <Typography variant="body2" color="text.secondary" data-id="element-40">
            Transaction ID: <strong data-id="element-41">{transaction.id}</strong>
          </Typography>
        </Box>

        <Tabs value={tabIndex} onChange={handleTabChange} sx={{
        mb: 3,
        borderBottom: 1,
        borderColor: 'divider',
        '& .MuiTab-root': {
          textTransform: 'none',
          fontWeight: 600,
          color: '#6B7280',
          '&.Mui-selected': {
            color: '#0052CC'
          }
        },
        '& .MuiTabs-indicator': {
          backgroundColor: '#0052CC'
        }
      }} data-id="element-42">
          <Tab label="Title Fetch Failure" data-id="element-43" />
          <Tab label="Amend Account" data-id="element-44" />
        </Tabs>

        <Stepper activeStep={activeStep} alternativeLabel sx={{
        mb: 4,
        '& .MuiStepIcon-root.Mui-active': {
          color: '#0052CC'
        },
        '& .MuiStepIcon-root.Mui-completed': {
          color: '#0B7923'
        }
      }} data-id="element-45">
          {steps.map(label => <Step key={label} data-id="element-46">
              <StepLabel data-id="element-47">{label}</StepLabel>
            </Step>)}
        </Stepper>

        {tabIndex === 0 && <>
            {activeStep === 0 && <Box data-id="element-48">
                <Paper variant="outlined" sx={{
            p: 2,
            mb: 3,
            bgcolor: '#F8FAFC'
          }} data-id="element-49">
                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom data-id="element-50">
                    Current Account / IBAN
                  </Typography>
                  <Typography variant="body1" fontWeight={500} sx={{
              color: 'text.secondary'
            }} data-id="element-51">
                    {transaction.beneficiaryIBAN}
                  </Typography>
                </Paper>

                <TextField label="Enter IBAN for Title Fetch" placeholder="Enter IBAN..." fullWidth required value={newAccount} onChange={e => {
            setNewAccount(e.target.value);
            setError(null);
          }} error={!!error} helperText={error} disabled={isLoading} data-id="element-52" />
              </Box>}

            {activeStep === 1 && <Box data-id="element-53">
                <Alert severity="info" sx={{
            mb: 3
          }} data-id="element-54">
                  Please verify if the fetched title matches the intended
                  beneficiary.
                </Alert>

                <Paper variant="outlined" sx={{
            p: 3,
            mb: 3,
            bgcolor: '#F0F9FF',
            borderColor: '#BAE6FD'
          }} data-id="element-55">
                  <Box sx={{
              mb: 2
            }} data-id="element-56">
                    <Typography variant="caption" color="text.secondary" display="block" gutterBottom data-id="element-57">
                      IBAN
                    </Typography>
                    <Typography variant="body1" fontWeight={600} color="primary.main" data-id="element-58">
                      {newAccount}
                    </Typography>
                  </Box>
                  <Box data-id="element-59">
                    <Typography variant="caption" color="text.secondary" display="block" gutterBottom data-id="element-60">
                      Fetched Account Title
                    </Typography>
                    <Typography variant="h6" fontWeight={700} color="text.primary" data-id="element-61">
                      {fetchedTitle}
                    </Typography>
                  </Box>
                </Paper>
              </Box>}

            {activeStep === 2 && <Box data-id="element-62">
                <Alert severity="success" sx={{
            mb: 3
          }} data-id="element-63">
                  Title verified. Please provide a reason for this amendment.
                  This will be sent to a checker for approval.
                </Alert>

                <Paper variant="outlined" sx={{
            p: 2,
            mb: 3,
            bgcolor: '#F8FAFC'
          }} data-id="element-64">
                  <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              mb: 1
            }} data-id="element-65">
                    <Typography variant="caption" color="text.secondary" data-id="element-66">
                      IBAN:
                    </Typography>
                    <Typography variant="body2" fontWeight={600} data-id="element-67">
                      {newAccount}
                    </Typography>
                  </Box>
                  <Box sx={{
              display: 'flex',
              justifyContent: 'space-between'
            }} data-id="element-68">
                    <Typography variant="caption" color="text.secondary" data-id="element-69">
                      Verified Title:
                    </Typography>
                    <Typography variant="body2" fontWeight={600} data-id="element-70">
                      {fetchedTitle}
                    </Typography>
                  </Box>
                </Paper>

                <TextField label="Amendment Reason" placeholder="Enter reason for amendment..." multiline rows={4} fullWidth required value={reason} onChange={e => {
            setReason(e.target.value);
            setError(null);
          }} error={!!error} helperText={error} disabled={isLoading} data-id="element-71" />
              </Box>}
          </>}

        {tabIndex === 1 && <>
            {activeStep === 0 && <Box data-id="element-72">
                <Paper variant="outlined" sx={{
            p: 2,
            mb: 3,
            bgcolor: '#F8FAFC'
          }} data-id="element-73">
                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom data-id="element-74">
                    Current Account / IBAN
                  </Typography>
                  <Typography variant="body1" fontWeight={500} sx={{
              textDecoration: 'line-through',
              color: 'text.secondary'
            }} data-id="element-75">
                    {transaction.beneficiaryIBAN}
                  </Typography>
                </Paper>

                <TextField label="New Account Number" placeholder="Enter new account number..." fullWidth required value={newAccount} onChange={e => {
            setNewAccount(e.target.value);
            setError(null);
          }} error={!!error} helperText={error} disabled={isLoading} data-id="element-76" />
              </Box>}

            {activeStep === 1 && <Box data-id="element-77">
                <Alert severity="info" sx={{
            mb: 3
          }} data-id="element-78">
                  Please provide a reason for this account number amendment.
                  This will be sent to a checker for approval.
                </Alert>

                <Paper variant="outlined" sx={{
            p: 2,
            mb: 3,
            bgcolor: '#F8FAFC'
          }} data-id="element-79">
                  <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              mb: 1
            }} data-id="element-80">
                    <Typography variant="caption" color="text.secondary" data-id="element-81">
                      New Account:
                    </Typography>
                    <Typography variant="body2" fontWeight={600} data-id="element-82">
                      {newAccount}
                    </Typography>
                  </Box>
                </Paper>

                <TextField label="Amendment Reason" placeholder="Enter reason for amendment..." multiline rows={4} fullWidth required value={reason} onChange={e => {
            setReason(e.target.value);
            setError(null);
          }} error={!!error} helperText={error} disabled={isLoading} data-id="element-83" />
              </Box>}
          </>}
      </DialogContent>
      <DialogActions sx={{
      borderTop: '1px solid #E5E7EB',
      p: 2
    }} data-id="element-84">
        <Button onClick={onClose} color="inherit" data-id="element-85">
          Cancel
        </Button>
        {activeStep > 0 && <Button onClick={handleBack} color="inherit" data-id="element-86">
            Back
          </Button>}
        <Button variant="contained" onClick={handleNext} disabled={isLoading || activeStep === 0 && !newAccount || activeStep === 1 && tabIndex === 0 && !fetchedTitle || activeStep === steps.length - 1 && !reason} sx={{
        backgroundColor: '#0052CC',
        '&:hover': {
          backgroundColor: '#003DA8'
        }
      }} data-id="element-87">
          {isLoading ? <CircularProgress size={24} color="inherit" data-id="element-88" /> : activeStep === steps.length - 1 ? 'Submit Amendment' : 'Next'}
        </Button>
      </DialogActions>
    </Dialog>;
};
export default AmendTransactionDialog;